import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { z } from "zod";
import { authClient } from "@/api/auth";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/field";
import { AuthLayout } from "@/features/auth/auth-layout";
import { checkNewPassword, passwordErrorOf } from "@/features/auth/password";
import { noindexHead } from "@/features/marketing/seo";
import { t } from "@/i18n";

export const Route = createFileRoute("/reset-password")({
  head: () => noindexHead(t("marketing.seo.forgot")),
  validateSearch: z.object({ token: z.string().optional(), error: z.string().optional() }),
  component: ResetPage,
});

function ResetPage() {
  const { token, error: linkError } = Route.useSearch();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(linkError ? t("auth.resetLinkInvalid") : null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    const weak = checkNewPassword(password);
    if (weak) { setError(weak); return; }
    setBusy(true); setError(null);
    const res = await authClient.resetPassword({ newPassword: password, token });
    setBusy(false);
    if (res.error) { setError(passwordErrorOf(res.error) ?? res.error.message ?? t("auth.resetFailed")); return; }
    void navigate({ to: "/login" });
  };

  return (
    <AuthLayout title={t("auth.reset")}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t("auth.newPassword")} hint={t("auth.passwordHint")}><Input type="password" autoComplete="new-password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {error && <p className="text-sm text-danger">{error}</p>}
        <Button type="submit" size="lg" loading={busy} disabled={!token}>{t("auth.reset")}</Button>
      </form>
    </AuthLayout>
  );
}
